import {
  useCallback,
  useState,
} from "react";

import {
  getQueriesApi,
  getQueryApi,
  createQueryApi,
  updateQueryApi,
  deleteQueryApi,
} from "../../../api/queries/queries.api";


const getErrorMessage = (error) => {

  return (
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong"
  );

};


export const useQueries = () => {

  const [queries, setQueries] = useState([]);

  const [query, setQuery] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const [loading, setLoading] = useState(false);

  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);

  const [error, setError] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | FETCH QUERIES
  |--------------------------------------------------------------------------
  */

  const fetchQueries = useCallback(
    async (params = {}) => {

      setLoading(true);
      setError(null);

      try {

        console.log(
          "[QUERIES] Fetching with params:",
          params
        );


        const response =
          await getQueriesApi(params);


        console.log(
          "[QUERIES] API RESPONSE:",
          response
        );


        /*
        |--------------------------------------------------------------------------
        | EXPECTED RESPONSE
        |--------------------------------------------------------------------------
        |
        | {
        |   success: true,
        |   message: "...",
        |   data: [],
        |   pagination: {}
        | }
        |
        */

        const queryList =
          Array.isArray(response?.data)
            ? response.data
            : [];


        const paginationData =
          response?.pagination || {
            page: params.page || 1,
            limit: params.limit || 20,
            total: queryList.length,
            totalPages:
              queryList.length > 0
                ? 1
                : 0,
          };


        console.log(
          "[QUERIES] QUERY LIST:",
          queryList
        );


        console.log(
          "[QUERIES] PAGINATION:",
          paginationData
        );


        setQueries(queryList);

        setPagination(paginationData);


        return response;

      } catch (err) {

        console.error(
          "[QUERIES] FETCH ERROR:",
          err
        );


        const message =
          getErrorMessage(err);


        setError(message);

        setQueries([]);

        setPagination({
          page: params.page || 1,
          limit: params.limit || 20,
          total: 0,
          totalPages: 0,
        });


        throw err;

      } finally {

        setLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | FETCH SINGLE QUERY
  |--------------------------------------------------------------------------
  */

  const fetchQuery = useCallback(
    async (id) => {

      if (!id) {
        return null;
      }


      setDetailsLoading(true);
      setError(null);


      try {

        console.log(
          "[QUERY DETAILS] ID:",
          id
        );


        const response =
          await getQueryApi(id);


        console.log(
          "[QUERY DETAILS] RESPONSE:",
          response
        );


        const result =
          response?.data || null;


        setQuery(result);


        return result;

      } catch (err) {

        console.error(
          "[QUERY DETAILS] ERROR:",
          err
        );


        const message =
          getErrorMessage(err);


        setError(message);

        setQuery(null);


        throw err;

      } finally {

        setDetailsLoading(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CREATE QUERY
  |--------------------------------------------------------------------------
  */

  const createQuery = useCallback(
    async (data) => {

      setSaving(true);
      setError(null);


      try {

        const response =
          await createQueryApi(data);


        console.log(
          "[QUERY CREATE] RESPONSE:",
          response
        );


        return response?.data ?? response;

      } catch (err) {

        const message =
          getErrorMessage(err);


        setError(message);


        throw err;

      } finally {

        setSaving(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | UPDATE QUERY
  |--------------------------------------------------------------------------
  */

  const updateQuery = useCallback(
    async (id, data) => {

      if (!id) {
        throw new Error(
          "Query ID is required"
        );
      }


      setSaving(true);
      setError(null);


      try {

        const response =
          await updateQueryApi(
            id,
            data
          );


        console.log(
          "[QUERY UPDATE] RESPONSE:",
          response
        );


        const updated =
          response?.data || null;


        setQuery(updated);


        setQueries((current) =>
          current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  ...updated,
                }
              : item
          )
        );


        return updated;

      } catch (err) {

        const message =
          getErrorMessage(err);


        setError(message);


        throw err;

      } finally {

        setSaving(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | DELETE QUERY
  |--------------------------------------------------------------------------
  */

  const deleteQuery = useCallback(
    async (id) => {

      if (!id) {
        throw new Error(
          "Query ID is required"
        );
      }


      setDeleting(true);
      setError(null);


      try {

        const response =
          await deleteQueryApi(id);


        console.log(
          "[QUERY DELETE] RESPONSE:",
          response
        );


        setQueries((current) =>
          current.filter(
            (item) =>
              item.id !== id
          )
        );


        setQuery((current) =>
          current?.id === id
            ? null
            : current
        );


        return response?.data ?? response;

      } catch (err) {

        const message =
          getErrorMessage(err);


        setError(message);


        throw err;

      } finally {

        setDeleting(false);

      }

    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CLEAR ERROR
  |--------------------------------------------------------------------------
  */

  const clearError = useCallback(
    () => {
      setError(null);
    },
    []
  );


  /*
  |--------------------------------------------------------------------------
  | CLEAR QUERY
  |--------------------------------------------------------------------------
  */

  const clearQuery = useCallback(
    () => {
      setQuery(null);
    },
    []
  );


  return {

    queries,

    query,

    pagination,

    loading,

    detailsLoading,

    saving,

    deleting,

    error,

    fetchQueries,

    fetchQuery,

    createQuery,

    updateQuery,

    deleteQuery,

    clearError,

    clearQuery,

  };

};